import { createEffect } from '@lib/state-engine';
import {
  warehouseMapper,
  warehouseWithBagsMapper,
} from '@@/warehouses/mappers/warehouse.mappers';
import { bagMapper } from '@@/bags/mappers/bag.mapper';
import { DiContainer } from '@@/di/di-container';
import { WarehousesService } from '@@/warehouses/services/warehouses.service';
import { IWarehousesService } from '@@/warehouses/services/IWarehousesService.interface';
import { BagsService, IBagsService } from '@@/bags/services/bags.service';
import { IWarehouse, IWarehouseStatistic, IWarehouseWithBags } from '@@/warehouses/ts';
import { IBag, IBagApi, IBagAdd, IFillWarehouseBagData } from '@@/bags/ts';
import { responseErrorInterceptorNotification } from '@@/common/utils/response-error-interceptor-notification.util';

const warehousesService = DiContainer.get(WarehousesService);
const bagsService = DiContainer.get(BagsService);

export const fetchAllWarehousesFx = createEffect('fetchAllWarehouses', {
  handler: async () => {
    const warehouses = await warehousesService.find();
    return warehouses.map(warehouseMapper);
  },
});

export const fetchWarehouseByIdFx = createEffect<IWarehouse['id'], IWarehouseWithBags>(
  'fetchWarehouseById',
  {
    handler: async (whId) => {
      const warehouse = await warehousesService.findOneById(whId);
      return warehouseWithBagsMapper(warehouse);
    },
  },
);

export const fetchWarehouseStatByIdFx = createEffect<
  IWarehouse['id'],
  IWarehouseStatistic
>('fetchWarehouseStatById', {
  handler: async (whId) => {
    if (!whId || whId === 0) return; 
    const statistic = await warehousesService.statistic(whId);
    return statistic;
  },
});

export const fetchBagByIdFx = createEffect<IBagApi['id'], IBag>('fetchBagById', {
  handler: async (bagId) => {
    const bag = await bagsService.findOneById(bagId);
    return bagMapper(bag);
  },
});

export const fetchRemoveWarehouseByIdFx = createEffect<
  IWarehouse['id'],
  ReturnType<IWarehousesService['update']>
>('fetchRemoveWarehouseById', {
  handler: (whId) =>
    responseErrorInterceptorNotification({
      handler: () => warehousesService.delete(whId),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});

export const fetchAddWarehouseBagFx = createEffect<
  IBagAdd,
  ReturnType<IBagsService['create']>
>('fetchAddWarehouseBag', {
  handler: (bag) =>
    responseErrorInterceptorNotification({
      handler: () => bagsService.create(bag),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});

export const fetchFillWarehouseBagFx = createEffect<
  { bagId: IBagApi['id']; data: IFillWarehouseBagData },
  ReturnType<IBagsService['update']>
>('fetchFillWarehouseBag', {
  // @ts-ignore
  handler: ({ bagId, data }) =>
    responseErrorInterceptorNotification({
      handler: () => bagsService.update(bagId, data),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});

export const fetchClearWarehouseBagFx = createEffect<
  IBag,
  ReturnType<IBagsService['update']>
>('fetchClearWarehouseBag', {
  handler: (bag) =>
    responseErrorInterceptorNotification({
      handler: () => bagsService.update(bag.id, { agriculture_id: null }),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});

export const fetchRemoveWarehouseBagFx = createEffect<
  IBagApi['id'],
  ReturnType<IBagsService['delete']>
>('fetchRemoveWarehouseBag', {
  handler: (bagId) =>
    responseErrorInterceptorNotification({
      handler: () => bagsService.delete(bagId),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});

export const fetchRenameWarehouseFx = createEffect<
  { name: string; id: number },
  ReturnType<any>
>('fetchRenameWarehouse', {
  handler: (bag) =>
    responseErrorInterceptorNotification({
      handler: async () => warehousesService.rename(bag.name, bag.id),
      catchHandler: (err, isResponse, body) => {
        throw err; // чтобы effector не считал, что эффект завершился успешно
      },
    }),
});
